import type { AeTemplateDetail, AeTemplateSection } from '@/types/assessmentEngine';
import { DEMOGRAPHICS_SECTION_CODE } from './templateConfigTypes';
import { sectionParticipatesInScoring } from './sectionScoring';

export interface StructureMoveTarget {
  sectionId: string;
  label: string;
}

/** Top-level content dimensions (excludes system demographics). */
export function topLevelContentSections(template: AeTemplateDetail): AeTemplateSection[] {
  return template.sections.filter((s) => s.code !== DEMOGRAPHICS_SECTION_CODE);
}

/** Sibling sections/subsections for reorder within the same parent. */
export function sectionSiblings(
  template: AeTemplateDetail,
  section: AeTemplateSection,
  parentSection?: AeTemplateSection
): AeTemplateSection[] {
  if (section.parent_section_id && parentSection) {
    return parentSection.subsections;
  }
  return topLevelContentSections(template);
}

export function subsectionMoveTargets(
  template: AeTemplateDetail,
  subsectionId: string,
  currentParentId: string
): StructureMoveTarget[] {
  return topLevelContentSections(template)
    .filter(
      (sec) =>
        sec.id !== currentParentId &&
        sec.id !== subsectionId &&
        sectionParticipatesInScoring(sec.scoring_config)
    )
    .map((sec) => ({ sectionId: sec.id, label: sec.name }));
}

/** Sections and subsections a question can be moved into (append at end). */
export function questionMoveTargets(
  template: AeTemplateDetail,
  currentSectionId: string
): StructureMoveTarget[] {
  const out: StructureMoveTarget[] = [];
  for (const sec of template.sections) {
    if (sec.code === DEMOGRAPHICS_SECTION_CODE) continue;
    if (sec.id !== currentSectionId) {
      out.push({ sectionId: sec.id, label: sec.name });
    }
    for (const sub of sec.subsections) {
      if (sub.id !== currentSectionId) {
        out.push({ sectionId: sub.id, label: `${sec.name} › ${sub.name}` });
      }
    }
  }
  return out;
}

/** Find a section or subsection anywhere in the template tree. */
export function findSectionById(
  template: AeTemplateDetail,
  sectionId: string
): AeTemplateSection | undefined {
  for (const sec of template.sections) {
    if (sec.id === sectionId) return sec;
    const sub = sec.subsections.find((s) => s.id === sectionId);
    if (sub) return sub;
  }
  return undefined;
}
